import { useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useTestRetrieval } from '../../../hooks/useSupport';
import { EmptyState } from '../../../components/common/EmptyState/EmptyState';
import { ErrorMessage } from '../../../components/common/ErrorMessage/ErrorMessage';
import { kbSourceTypeLabelKey } from './kbMeta';
import '../Roles/roles.css';

/** How much of a chunk's text to show inline before truncating. */
const EXCERPT_LENGTH = 300;

/**
 * The tool an agent reaches for when the bot answers a question oddly: run
 * the same retrieval the assistant would run, and see exactly which chunks
 * came back and with what score. An empty result is not a broken query —
 * "nothing passed the score floor" is the answer that explains an
 * escalation, so it renders as an empty state, never an error.
 *
 * There is deliberately no language control. Retrieval does not filter by
 * language (the corpus is mixed on purpose and the embeddings are
 * cross-lingual — see `KbRepository.vectorSearch`), so a selector here would
 * change nothing while flipping it. On the one panel an agent uses to work out
 * why the bot answered oddly, an inert control is worse than no control: it
 * invites the conclusion that the English documentation is unreachable.
 */
export function TestRetrievalPanel() {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const { mutate, data, isPending, isError, error } = useTestRetrieval();

  const run = () => {
    const trimmed = query.trim();
    if (!trimmed) return;
    mutate({ query: trimmed });
  };

  const hasRun = data !== undefined;
  const chunks = data?.chunks ?? [];

  const errorMessage = isError
    ? error instanceof Error
      ? error.message
      : t('dashboard.kb.retrievalError')
    : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 720 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        {t('dashboard.kb.testRetrieval')}
      </Typography>

      {/* Native field: the shared MuiTextField override paints outlined
          fields dark for the mobile app, which is unreadable here. */}
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
        <input
          type="search"
          className="filter-search-input"
          placeholder={t('dashboard.kb.query')}
          aria-label={t('dashboard.kb.query')}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') run();
          }}
          disabled={isPending}
        />
        <button
          type="button"
          className="primary-action-btn"
          onClick={run}
          disabled={isPending || query.trim().length === 0}
        >
          {t('dashboard.kb.run')}
        </button>
      </div>

      {errorMessage ? (
        <ErrorMessage message={errorMessage} />
      ) : hasRun && chunks.length === 0 ? (
        <EmptyState icon="search_off" message={t('dashboard.kb.noHits')} />
      ) : chunks.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {chunks.map((chunk, index) => (
            <Box
              key={`${chunk.ref}-${index}`}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                  {chunk.ref}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t(kbSourceTypeLabelKey(chunk.sourceType), { defaultValue: chunk.sourceType })}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                  {chunk.score.toFixed(3)}
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {chunk.text.slice(0, EXCERPT_LENGTH)}
                {chunk.text.length > EXCERPT_LENGTH ? '…' : ''}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : null}
    </Box>
  );
}

export default TestRetrievalPanel;
